import type { OAuthTokens } from './tokens.ts';

export const ACCESS_TOKEN_KEY = 'access_token';
export const REFRESH_TOKEN_KEY = 'refresh_token';
export const ACCESS_TOKEN_EXPIRES_AT_KEY = 'oauth_access_token_expires_at';

const hasWindow = () => typeof window !== 'undefined';

export const safeGetItem = (key: string): string | null => {
  if (!hasWindow()) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

export const safeSetItem = (key: string, value: string): void => {
  if (!hasWindow()) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore quota or access errors; caller should handle missing tokens.
  }
};

export const safeRemoveItem = (key: string): void => {
  if (!hasWindow()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore removal errors.
  }
};

export const persistTokens = (tokens: OAuthTokens): void => {
  if (!tokens.accessToken || !tokens.refreshToken) return;

  safeSetItem(ACCESS_TOKEN_KEY, tokens.accessToken);
  safeSetItem(REFRESH_TOKEN_KEY, tokens.refreshToken);

  if (tokens.expiresIn && Number.isFinite(tokens.expiresIn)) {
    const expiresAt = Date.now() + tokens.expiresIn * 1000;
    safeSetItem(ACCESS_TOKEN_EXPIRES_AT_KEY, String(expiresAt));
  }
};

export const getAccessToken = (): string | null => safeGetItem(ACCESS_TOKEN_KEY);

export const getRefreshToken = (): string | null => safeGetItem(REFRESH_TOKEN_KEY);

export const getAccessTokenExpiresAt = (): number | null => {
  const raw = safeGetItem(ACCESS_TOKEN_EXPIRES_AT_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

export const isAccessTokenExpired = (): boolean => {
  const expiresAt = getAccessTokenExpiresAt();
  if (!expiresAt) return false;
  return Date.now() >= expiresAt;
};

export const clearTokens = (): void => {
  safeRemoveItem(ACCESS_TOKEN_KEY);
  safeRemoveItem(REFRESH_TOKEN_KEY);
  safeRemoveItem(ACCESS_TOKEN_EXPIRES_AT_KEY);
};

